import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { FormInputSelect } from '../../../../common/formInputsV2/FormInputSelect';
import { FormInputPrice } from '../../../../common/formInputsV2/FormInputPrice';
import { calculateAge, garanzie_ubicazione } from '../../../../utils/globalCasaConstants';
import {
  GaranzieContainer,
  SectionContainer,
  SectionTitle,
  GaranziaItem,
  GaranziaHeader,
  CheckboxWrapper,
  ParameterContainer,
} from './GaranzieStep.styles';

function GaranzieStep({
  formDataFlow,
  control,
  fields,
  replace,
  update,
  watchedGuarantees,
}) {
  useEffect(() => {
    if (!formDataFlow.residential_use_types) return;

    const shouldIncludeGaranzia = (garanzia) => {
      if (!['141', '142', '143', '144'].includes(formDataFlow.residential_use_types) && garanzia.cod_pacchetto === 'INCF') {
        return false;
      }
      if (!['141', '142', '143', '145', '146'].includes(formDataFlow.residential_use_types) && garanzia.cod_pacchetto === 'INCC') {
        return false;
      }
      if (['142', '143', '144', '146'].includes(formDataFlow.residential_use_types) && garanzia.group_garanzia === 'ASSISTENZA') {
        return false;
      }
      if (['144'].includes(formDataFlow.residential_use_types) && garanzia.group_garanzia === 'FURTO') {
        return false;
      }
      if (formDataFlow.interested_in_building_fire_coverage !== '1' && garanzia.is_fabbricato === true) {
        return false;
      }
      if (formDataFlow.interested_in_contents_fire_coverage !== '1' && garanzia.is_contenuto === true) {
        return false;
      }
      if (calculateAge(formDataFlow.date_of_birth) >= 65 && garanzia.cod_pacchetto === 'LS') {
        return false;
      }
      // Contents add-ons depend on the building ones, which are hidden when
      // building fire cover is not requested, so the price can't be calculated.
      if (formDataFlow.interested_in_building_fire_coverage !== '1' && ['EAC', 'SPC', 'ESC'].includes(garanzia.cod_pacchetto)) {
        return false;
      }
      return true;
    };

    const currentValues = watchedGuarantees || [];

    const filteredGaranzie = formDataFlow.insurance_formula === '113'
      ? garanzie_ubicazione.filter((g) => g.cod_pacchetto !== 'TLPC')
      : garanzie_ubicazione;

    const availableGaranzie = filteredGaranzie
      .filter(shouldIncludeGaranzia)
      .map((g) => {
        const existing = currentValues.find(
          (f) => f.cod_garanzie?.join('-') === g.cod_garanzie.join('-'),
        );

        const isRequiredIncendioFabbricato = formDataFlow.interested_in_building_fire_coverage === '1'
          && g.cod_pacchetto === 'INCF';

        const isRequiredIncendioContenuto = formDataFlow.interested_in_contents_fire_coverage === '1'
          && g.cod_pacchetto === 'INCC';

        const isRequired = isRequiredIncendioFabbricato || isRequiredIncendioContenuto;

        return {
          cod_garanzie: g.cod_garanzie,
          cod_pacchetto: g.cod_pacchetto === 'null' ? null : g.cod_pacchetto,
          desc_garanzia: g.desc_garanzia,
          group_garanzia: g.group_garanzia,
          selected: isRequired ? true : existing ? existing.selected : false,
          disabled: isRequired,
          parameters: g.cod_pacchetto === 'INCC'
            ? { ...(existing ? existing.parameters : {}), forma_copertura: '1' }
            : existing ? existing.parameters : {},
          building_value: existing ? existing.building_value : '',
          parametersConfig: g.parameters,
        };
      });

    if (formDataFlow.interested_in_building_fire_coverage === '1' && formDataFlow.interested_in_contents_fire_coverage !== '1') {
      replace(availableGaranzie.filter((g) => !['AC', 'TC'].includes(g.cod_pacchetto)));
    } else {
      replace(availableGaranzie);
    }
  }, [
    formDataFlow.residential_use_types,
    formDataFlow.interested_in_building_fire_coverage,
    formDataFlow.interested_in_contents_fire_coverage,
    formDataFlow.insurance_formula,
  ]);

  const handleCheckboxChange = (index) => {
    const field = fields[index];

    const deselectField = (code) => {
      const i = fields.findIndex((g) => g.cod_garanzie?.includes(code));
      if (i !== -1 && fields[i].selected) {
        update(i, { ...fields[i], selected: false });
      }
    };

    if (field.cod_garanzie.includes('TF') && field.selected) {
      deselectField('TC');
      deselectField('AF');
      deselectField('AC');
    }
    if (field.cod_garanzie.includes('TC') && field.selected) {
      deselectField('AF');
      deselectField('AC');
    }
    if (field.cod_garanzie.includes('AF') && field.selected) {
      deselectField('AC');
    }

    update(index, { ...field, selected: !field.selected });
  };

  const handleParameterChange = (index, paramName, value) => {
    const field = fields[index];
    const updatedParameters = { ...field.parameters, [paramName]: value };

    if (paramName === 'massimale' && !['8', '9'].includes(value) && field.cod_garanzie.includes('FU')) {
      updatedParameters.estensione_sottolimite_pregiati = '1';
      updatedParameters.estensione_sottolimite_gioielli = '1';
    }

    update(index, { ...field, parameters: updatedParameters });
  };

  const hasParameterError = (field, paramName) => field.selected && !field.parameters[paramName];

  const hasBuildingValueError = (field, buildingValue) => {
    const isTerremoto = field.cod_garanzie.includes('TF');
    return field.selected && isTerremoto && !buildingValue;
  };

  const groupFieldsByCategory = () => {
    const grouped = {
      INCENDIO: [],
      'RESPONSABILITÀ CIVILE': [],
      LASTRE: [],
      CATASTROFALE: [],
      FURTO: [],
      'TUTELA LEGALE': [],
      ASSISTENZA: [],
      'LIFE STYLE': [],
    };

    fields.forEach((field, index) => {
      if (grouped[field.group_garanzia]) {
        grouped[field.group_garanzia].push({ ...field, index });
      }
    });

    return grouped;
  };

  const groupedFields = groupFieldsByCategory();

  useEffect(() => {
    if (!watchedGuarantees) return;

    const indexRGF = watchedGuarantees.findIndex((g) => g.cod_garanzie?.includes('RGF'));
    const indexSPF = watchedGuarantees.findIndex((g) => g.cod_garanzie?.includes('SPF'));
    const RGF = watchedGuarantees[indexRGF];
    const SPF = watchedGuarantees[indexSPF];

    if (RGF && SPF) {
      if (RGF.selected && (!SPF.selected || !SPF.disabled)) {
        update(indexSPF, { ...SPF, selected: true, disabled: true });
      }
      if (!RGF.selected && SPF.disabled) {
        update(indexSPF, { ...SPF, disabled: false });
      }
    }

    const indexTF = watchedGuarantees.findIndex((g) => g.cod_garanzie?.includes('TF'));
    const indexTC = watchedGuarantees.findIndex((g) => g.cod_garanzie?.includes('TC'));
    const indexAF = watchedGuarantees.findIndex((g) => g.cod_garanzie?.includes('AF'));
    const indexAC = watchedGuarantees.findIndex((g) => g.cod_garanzie?.includes('AC'));

    const TF = watchedGuarantees[indexTF];
    const TC = watchedGuarantees[indexTC];
    const AF = watchedGuarantees[indexAF];
    const AC = watchedGuarantees[indexAC];

    if ((TC && TC.selected) && (TF && !TF.selected)) {
      update(indexTF, { ...TF, selected: true });
      return;
    }

    if ((AF && AF.selected) && (TC && !TC.selected) && (TF && !TF.selected)) {
      update(indexTF, { ...TF, selected: true });
      update(indexTC, { ...TC, selected: true });
    }

    if ((AC && AC.selected) && (AF && !AF.selected) && (TC && !TC.selected) && (TF && !TF.selected)) {
      update(indexTF, { ...TF, selected: true });
      update(indexTC, { ...TC, selected: true });
      update(indexAF, { ...AF, selected: true });
    }

    if ((AF && AF.selected) && (TC && !TC.selected)) {
      update(indexTC, { ...TC, selected: true });
    }
    if ((AF && AF.selected) && (TF && !TF.selected)) {
      update(indexTF, { ...TF, selected: true });
    }

    if ((AC && AC.selected) && (AF && !AF.selected) && (TC && !TC.selected)) {
      update(indexTC, { ...TC, selected: true });
      update(indexAF, { ...AF, selected: true });
    }

    if ((AC && AC.selected) && (AF && !AF.selected)) {
      update(indexAF, { ...AF, selected: true });
    }
  }, [watchedGuarantees, update]);

  return (
    <GaranzieContainer>
      {Object.entries(groupedFields).map(([category, categoryFields]) => {
        if (categoryFields.length === 0) {
          return null;
        }

        return (
          <SectionContainer key={category}>
            <SectionTitle>{category}</SectionTitle>
            {categoryFields.map((field) => {
              const buildingValue = watchedGuarantees?.[field.index]?.building_value;

              const hasErrors = field.parametersConfig?.some(
                (param) => hasParameterError(field, param.cod_param),
              ) || hasBuildingValueError(field, buildingValue);
              const hasSuccess = field.selected && !hasErrors;

              const garanziaKey = field.cod_garanzie.join('-');
              const isTerremoto = field.cod_garanzie.includes('TF');

              return (
                <GaranziaItem key={field.id} hasError={hasErrors} hasSuccess={hasSuccess}>
                  <GaranziaHeader>
                    <CheckboxWrapper>
                      <input
                        type="checkbox"
                        id={`garanzia-${garanziaKey}`}
                        checked={field.selected || false}
                        onChange={() => handleCheckboxChange(field.index)}
                        disabled={field.disabled || false}
                      />
                      <label htmlFor={`garanzia-${garanziaKey}`}>
                        {field.desc_garanzia}
                      </label>
                    </CheckboxWrapper>
                  </GaranziaHeader>

                  {field.selected && (
                    <>
                      {isTerremoto && (
                        <ParameterContainer>
                          <Controller
                            name={`location_guarantees.${field.index}.building_value`}
                            control={control}
                            render={({ field: controllerField }) => (
                              <FormInputPrice
                                label="VALORE FABBRICATO"
                                placeholder="Inserisci valore fabbricato"
                                value={controllerField.value || ''}
                                onChange={controllerField.onChange}
                                error={
                                  hasBuildingValueError(field, buildingValue)
                                    ? 'Campo obbligatorio'
                                    : ''
                                }
                                valid={field.selected && Boolean(buildingValue)}
                              />
                            )}
                          />
                        </ParameterContainer>
                      )}

                      {field.parametersConfig?.length > 0 && (
                        <ParameterContainer>
                          {field.parametersConfig.map((param, paramIndex) => {
                            const isSubLimitParam = param.cod_param === 'estensione_sottolimite_pregiati'
                              || param.cod_param === 'estensione_sottolimite_gioielli';
                            const isMassimaleRestricted = !['8', '9'].includes(field.parameters.massimale);
                            const isDisabled = isSubLimitParam && isMassimaleRestricted;

                            return (
                              <FormInputSelect
                                key={`${garanziaKey}-param-${paramIndex}`}
                                label={param.desc_parameter.toUpperCase()}
                                placeholder={`Seleziona ${param.desc_parameter}`}
                                options={param.options.map((opt) => ({
                                  id: opt.valore,
                                  name: opt.label,
                                }))}
                                value={field.parameters[param.cod_param] || ''}
                                onChange={(value) => handleParameterChange(field.index, param.cod_param, value)}
                                error={
                                  hasParameterError(field, param.cod_param)
                                    ? 'Campo obbligatorio'
                                    : ''
                                }
                                valid={
                                  field.selected && field.parameters[param.cod_param]
                                }
                                disabled={isDisabled}
                              />
                            );
                          })}
                        </ParameterContainer>
                      )}
                    </>
                  )}
                </GaranziaItem>
              );
            })}
          </SectionContainer>
        );
      })}
    </GaranzieContainer>
  );
}

export default GaranzieStep;
